import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { User } from "../../../Types/UnitFeatureTypes";


export interface FetchUserInfoState {
  request: {
      token: string | null;
      loading: boolean;
      error: string | null;
      data: User | null;
  };
}

export enum Types {
  FETCH_USER_REQUEST = "FETCH_USER_REQUEST",
  FETCH_USER_SUCCESS = "FETCH_USER_SUCCESS",
  FETCH_USER_FAILURE = "FETCH_USER_FAILURE",
  VERIFY_USER_REQUEST = "VERIFY_USER_REQUEST",
  VERIFY_USER_SUCCESS = "VERIFY_USER_SUCCESS",
  VERIFY_USER_FAILURE = "VERIFY_USER_FAILURE",
  LOGOUT_USER_REQUEST = "LOGOUT_USER_REQUEST"
}

const userLoginAction = createAsyncAction(
  Types.FETCH_USER_REQUEST,
  Types.FETCH_USER_SUCCESS,
  Types.FETCH_USER_FAILURE,
)
<
  { email: string, password: string },
  { token: string },
  { message: string }
>();

const verifyUserTokenAction = createAsyncAction(
  Types.VERIFY_USER_REQUEST,
  Types.VERIFY_USER_SUCCESS,
  Types.VERIFY_USER_FAILURE,
)
<
  { token: string },
  { user: User },
  { message: string }
>();

const logoutUser = createAction(Types.LOGOUT_USER_REQUEST)();

export const Actions = { userLoginAction, verifyUserTokenAction, logoutUser };

export const initialState: FetchUserInfoState = {
  request: {
    loading: false,
    error: null as string | null,
    data: null,
    token: null
  }
};

export type FetchUserState = typeof initialState;

const fetchBookingRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.data = null;

    return draft;
  });

const fetchBookingSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.userLoginAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.token = payload.token;
    if (draft.request.data) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchBookingFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.userLoginAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const verifyUserRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.data = null;
  });

const verifyUserSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.verifyUserTokenAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.data = payload.user;
    if (draft.request.data) {
      draft.request.loading = false;
    }
  });

const verifyUserFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.verifyUserTokenAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
  });

const logoutUserRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.token = null;
    draft.request.data = null;
    localStorage.removeItem('token');

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.userLoginAction.request, fetchBookingRequest)
  .handleAction(Actions.userLoginAction.success, fetchBookingSuccess)
  .handleAction(Actions.userLoginAction.failure, fetchBookingFailure)
  .handleAction(Actions.verifyUserTokenAction.request, verifyUserRequest)
  .handleAction(Actions.verifyUserTokenAction.success, verifyUserSuccess)
  .handleAction(Actions.verifyUserTokenAction.failure, verifyUserFailure)
  .handleAction(Actions.logoutUser, logoutUserRequest);